import { useMemo } from "react"
import { useSelector } from "react-redux"
import { filterVitalItems, getVitalItem } from "./utils"
import { AlertModalType } from "./index"
import { IconColor, IconProps } from "@/components/shared/Icon"
import { selectTimezone } from "@/store/facilitiesSlice"
import {
   AlertOxygenLevel,
   AlertThreshold,
   AlertVariant,
   AlertVitals,
   ProcessingMethod,
   TypefaceVariant,
} from "@/types/alert"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import { TypefaceColors } from "@/types/index"
import { relationalTime } from "@/utils/alerts"

interface UseAlertDataProps {
   alertVariant: keyof typeof AlertVariant
   typefaceColor: keyof typeof TypefaceVariant
   systemConditionType: AlertSystemConditionType | null
   dateTime: string
   type?: AlertModalType
   vitals: AlertVitals
   heartRateThreshold: AlertThreshold
   respiratoryRateThreshold?: AlertThreshold
   oxygenLevelThreshold?: AlertOxygenLevel
}

export const useAlertData = ({
   alertVariant,
   typefaceColor,
   systemConditionType,
   dateTime,
   type,
   vitals,
   heartRateThreshold,
   respiratoryRateThreshold,
   oxygenLevelThreshold,
}: UseAlertDataProps) => {
   const timezone = useSelector(selectTimezone)
   const informativeTime = useMemo(
      () => relationalTime(dateTime, timezone),
      [dateTime, timezone]
   )

   const modalVariantClassName = useMemo(
      () => `--${AlertVariant[alertVariant as keyof typeof AlertVariant]}`,
      [alertVariant]
   )

   const typefaceClassName = useMemo(
      () =>
         `--${TypefaceVariant[typefaceColor as keyof typeof TypefaceVariant]}`,
      [typefaceColor]
   )

   const typefaceIconColor = useMemo(
      () =>
         typefaceColor === TypefaceColors.WHITE
            ? IconColor.WHITE
            : IconColor.GUNMETAL,
      [typefaceColor]
   )

   const iconName: IconProps["name"] = useMemo(() => {
      switch (systemConditionType) {
         case AlertSystemConditionType.NO_VITALS:
            return "noVitalSign"
         case AlertSystemConditionType.BROKEN_WEARABLE:
            return "brokenWatch"
         default:
            return "risk"
      }
   }, [systemConditionType])

   const vitalsDisplay = useMemo(() => {
      if (type === AlertModalType.CELL) {
         const {
            lowHeartRate,
            highHeartRate,
            lowRespiratoryRate,
            highRespiratoryRate,
         } = vitals || {}

         return filterVitalItems([
            getVitalItem(
               "heartRate",
               lowHeartRate,
               highHeartRate,
               heartRateThreshold
            ),
            respiratoryRateThreshold
               ? getVitalItem(
                    "respiratoryRate",
                    lowRespiratoryRate,
                    highRespiratoryRate,
                    respiratoryRateThreshold
                 )
               : null,
         ])
      }

      const { lowHeartRate, highHeartRate, lowOxygenLevel } = vitals || {}

      const oxygenItem =
         lowOxygenLevel && oxygenLevelThreshold
            ? {
                 iconName: "oxygenLevel" as IconProps["name"],
                 arrowIcon: "lowerThreshold" as const,
                 thresholdValue: oxygenLevelThreshold.low,
                 thresholdProcessingMethod: ProcessingMethod.STATIC,
              }
            : null

      return filterVitalItems([
         getVitalItem(
            "heartRate",
            lowHeartRate,
            highHeartRate,
            heartRateThreshold
         ),
         oxygenItem,
      ])
   }, [
      type,
      vitals,
      oxygenLevelThreshold,
      heartRateThreshold,
      respiratoryRateThreshold,
   ])

   return {
      informativeTime,
      modalVariantClassName,
      typefaceClassName,
      typefaceIconColor,
      iconName,
      vitalsDisplay,
   }
}
